/**
 * React Query hooks for the Answer module (M-ANSWER.3).
 *
 * Mirrors the shape of `use-quote.ts`. List + detail queries with live-poll
 * cadences for in-flight statuses, plus mutating helpers that invalidate
 * the right query keys on success.
 */
import { useAuth } from "@clerk/clerk-react";
import type {
  AnswerDocumentResponse,
  AnswerLibraryEntryResponse,
  AnswerQuestionnaireDetailResponse,
  AnswerQuestionnaireResponse,
  AnswerResponseRecord,
  CreateAnswerQuestionnaireRequest,
  GenerateAnswerQuestionnaireResponse,
  ImportAnswerQuestionsRequest,
  PromoteAnswerLibraryRequest,
  ReviewAnswerResponseRequest,
} from "@simpliwork/api-schema";
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { ApiError } from "@/lib/api-client";
import {
  type AnswerDocumentList,
  type AnswerLibraryList,
  type AnswerQuestionList,
  type AnswerQuestionnaireList,
  createAnswerQuestionnaire,
  deleteAnswerDocument,
  deleteAnswerLibrary,
  deleteAnswerQuestionnaire,
  fetchAnswerDocument,
  fetchAnswerDocuments,
  fetchAnswerLibrary,
  fetchAnswerQuestionnaire,
  fetchAnswerQuestionnaires,
  fetchAnswerQuestions,
  fetchAnswerResponse,
  generateAnswerQuestionnaire,
  importAnswerQuestions,
  promoteAnswerLibrary,
  reviewAnswerResponse,
  uploadAnswerDocument,
} from "@/lib/answer-api";

export const answerKeys = {
  all: ["answer"] as const,
  documents: () => ["answer", "documents"] as const,
  document: (id: string) => ["answer", "documents", id] as const,
  questionnaires: () => ["answer", "questionnaires"] as const,
  questionnaire: (id: string) => ["answer", "questionnaires", id] as const,
  questions: (questionnaireId: string) =>
    ["answer", "questionnaires", questionnaireId, "questions"] as const,
  response: (id: string) => ["answer", "answers", id] as const,
  library: () => ["answer", "library"] as const,
};

type QErr = ApiError | Error;

const DOCUMENT_POLL_MS = 2500;
const QUESTIONNAIRE_POLL_MS = 3000;

// ── documents ──────────────────────────────────────────────────────────────

function anyDocInFlight(data: AnswerDocumentList | undefined): boolean {
  if (!data) return false;
  return data.items.some((d) => d.status === "pending" || d.status === "processing");
}

export function useAnswerDocuments(): UseQueryResult<AnswerDocumentList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: answerKeys.documents(),
    queryFn: () => fetchAnswerDocuments({ getToken: () => getToken() }),
    refetchInterval: (query) => (anyDocInFlight(query.state.data) ? DOCUMENT_POLL_MS : false),
  });
}

export function useAnswerDocument(id: string): UseQueryResult<AnswerDocumentResponse, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: answerKeys.document(id),
    queryFn: () => fetchAnswerDocument(id, { getToken: () => getToken() }),
    enabled: id.length > 0,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return false;
      return d.status === "pending" || d.status === "processing" ? DOCUMENT_POLL_MS : false;
    },
  });
}

export function useUploadAnswerDocument(): UseMutationResult<
  AnswerDocumentResponse,
  QErr,
  { file: File; title?: string }
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, title }) =>
      uploadAnswerDocument(file, title ?? null, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: answerKeys.documents() }),
  });
}

export function useDeleteAnswerDocument(): UseMutationResult<void, QErr, string> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteAnswerDocument(id, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: answerKeys.documents() }),
  });
}

// ── questionnaires ─────────────────────────────────────────────────────────

export function useAnswerQuestionnaires(): UseQueryResult<AnswerQuestionnaireList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: answerKeys.questionnaires(),
    queryFn: () => fetchAnswerQuestionnaires({ getToken: () => getToken() }),
  });
}

export function useAnswerQuestionnaire(
  id: string,
): UseQueryResult<AnswerQuestionnaireDetailResponse, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: answerKeys.questionnaire(id),
    queryFn: () => fetchAnswerQuestionnaire(id, { getToken: () => getToken() }),
    enabled: id.length > 0,
    // Poll while the questionnaire is generating so the UI reflects newly-
    // drafted responses live without a manual refresh. Status flips to
    // `ready` / `failed` when the orchestrator finishes (M-ANSWER.4).
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return false;
      return d.status === "generating" ? QUESTIONNAIRE_POLL_MS : false;
    },
  });
}

export function useCreateAnswerQuestionnaire(): UseMutationResult<
  AnswerQuestionnaireResponse,
  QErr,
  CreateAnswerQuestionnaireRequest
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createAnswerQuestionnaire(body, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: answerKeys.questionnaires() }),
  });
}

export function useDeleteAnswerQuestionnaire(): UseMutationResult<void, QErr, string> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteAnswerQuestionnaire(id, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: answerKeys.questionnaires() }),
  });
}

export function useGenerateAnswerQuestionnaire(): UseMutationResult<
  GenerateAnswerQuestionnaireResponse,
  QErr,
  string
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => generateAnswerQuestionnaire(id, { getToken: () => getToken() }),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: answerKeys.questionnaire(id) });
      qc.invalidateQueries({ queryKey: answerKeys.questionnaires() });
    },
  });
}

// ── questions ──────────────────────────────────────────────────────────────

export function useAnswerQuestions(
  questionnaireId: string,
): UseQueryResult<AnswerQuestionList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: answerKeys.questions(questionnaireId),
    queryFn: () => fetchAnswerQuestions(questionnaireId, { getToken: () => getToken() }),
    enabled: questionnaireId.length > 0,
  });
}

export function useImportAnswerQuestions(): UseMutationResult<
  AnswerQuestionList,
  QErr,
  { questionnaireId: string; body: ImportAnswerQuestionsRequest }
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ questionnaireId, body }) =>
      importAnswerQuestions(questionnaireId, body, { getToken: () => getToken() }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: answerKeys.questions(vars.questionnaireId) });
      qc.invalidateQueries({ queryKey: answerKeys.questionnaire(vars.questionnaireId) });
    },
  });
}

// ── responses (review) ────────────────────────────────────────────────────

export function useAnswerResponse(id: string): UseQueryResult<AnswerResponseRecord, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: answerKeys.response(id),
    queryFn: () => fetchAnswerResponse(id, { getToken: () => getToken() }),
    enabled: id.length > 0,
  });
}

export function useReviewAnswerResponse(): UseMutationResult<
  AnswerResponseRecord,
  QErr,
  { id: string; body: ReviewAnswerResponseRequest }
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) =>
      reviewAnswerResponse(id, body, { getToken: () => getToken() }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: answerKeys.response(vars.id) });
      // Hydrated questionnaire detail caches the response too; invalidate
      // the parent so the review UI updates without a hard refresh.
      qc.invalidateQueries({ queryKey: answerKeys.questionnaires() });
      qc.invalidateQueries({ queryKey: ["answer", "questionnaires"], type: "all" });
    },
  });
}

// ── library ────────────────────────────────────────────────────────────────

export function useAnswerLibrary(): UseQueryResult<AnswerLibraryList, QErr> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: answerKeys.library(),
    queryFn: () => fetchAnswerLibrary({ getToken: () => getToken() }),
  });
}

export function usePromoteAnswerLibrary(): UseMutationResult<
  AnswerLibraryEntryResponse,
  QErr,
  PromoteAnswerLibraryRequest
> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => promoteAnswerLibrary(body, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: answerKeys.library() }),
  });
}

export function useDeleteAnswerLibrary(): UseMutationResult<void, QErr, string> {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteAnswerLibrary(id, { getToken: () => getToken() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: answerKeys.library() }),
  });
}
